import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * EmptyState — calm, generous, never apologetic. Used when a list is
 * empty, a search returns nothing, or a feature isn't activated yet.
 * Supports an optional primary action button slot.
 */
export function EmptyState({
  Icon,
  title,
  description,
  action,
  tone = "neutral",
  className,
}: {
  Icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  tone?: "neutral" | "success";
  className?: string;
}) {
  const ringClass =
    tone === "success"
      ? "bg-[color-mix(in_oklab,var(--success)_18%,transparent)] text-[var(--success)]"
      : "bg-[var(--bg-subtle)] text-[var(--fg-muted)]";

  return (
    <div className={cn("py-14 px-6 grid place-items-center text-center", className)}>
      <div className={cn("h-14 w-14 grid place-items-center rounded-full mb-4", ringClass)}>
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="text-[16px] font-semibold tracking-[-0.005em] text-[var(--fg)]">
        {title}
      </div>
      {description && (
        <p className="mt-1.5 max-w-[420px] text-[13.5px] text-[var(--fg-subtle)] leading-snug">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
